import type { FC, RefAttributes } from 'react';
import { useCallback, useEffect, useRef, useState, Fragment } from 'react';

import { L10nText } from '../../../../../services/uiTranslations/reactBindings/L10nText';
import type { Message } from '../../../../../store/slices/chat/types';
import { IconButton, Icons } from '../../../../controls/iconButton/IconButton';
import { MessageBubble } from '../messageBubble/MessageBubble';

import styles from './MessagesWrapper.module.css';

export interface MessagesWrapperProps extends RefAttributes<HTMLDivElement> {
  messages: Message[];
  currentUserId: string;
}

export const MessagesWrapper: FC<MessagesWrapperProps> = ({ messages, currentUserId }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [unreadMessageId, setUnreadMessageId] = useState<string | null>(null);

  const lastMessage = messages[0];
  // Store last message in ref for performance optimisation
  const lastMessageRef = useRef(lastMessage);

  useEffect(() => {
    lastMessageRef.current = lastMessage;
  }, [lastMessage]);

  const scrollToBottom = useCallback((): void => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollTop = 0;
      setUnreadMessageId(null);
    }
  }, []);

  // Automatically scroll to bottom when current user posts a message
  useEffect(() => {
    if (lastMessage?.userId === currentUserId) {
      scrollToBottom();
    }
  }, [currentUserId, lastMessage, scrollToBottom]);

  const isNearBottom = (element: HTMLDivElement): boolean => Math.abs(element.scrollTop) <= 45; // Magic threshold of message and unread message indicator heights combined

  // Mark last message as unread when user scrolls up
  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (wrapper) {
      const handleScroll = (): void => {
        if (!isNearBottom(wrapper) && !unreadMessageId && lastMessageRef.current) {
          setUnreadMessageId(lastMessageRef.current.messageId);
        }

        if (isNearBottom(wrapper)) {
          setUnreadMessageId(null);
        }
      };

      wrapper.addEventListener('scroll', handleScroll);

      return (): void => {
        wrapper.removeEventListener('scroll', handleScroll);
      };
    }
  }, [unreadMessageId]);

  return (
    <div className={styles.container}>
      <div className={styles.messagesWrapper} ref={wrapperRef}>
        {messages.map((message: Message) => (
          <Fragment key={message.messageId}>
            {message.messageId === unreadMessageId ? (
              <div className={styles.messagesWrapper__unreadMessage}>
                <L10nText tk={'UNREAD_MESSAGES'} />
              </div>
            ) : null}

            <div className={styles.messagesWrapper__message}>
              <MessageBubble {...message} currentUserId={currentUserId} />
            </div>
          </Fragment>
        ))}
      </div>

      {wrapperRef.current && !isNearBottom(wrapperRef.current) ? (
        <div className={styles.messagesWrapper__scrollButton}>
          <IconButton icon={Icons.ChevronDown} large onClick={scrollToBottom} />
        </div>
      ) : null}
    </div>
  );
};

MessagesWrapper.displayName = 'MessagesWrapper';
